/**
 * A client's own survey file (SPSS .sav or CSV) as a data source of a crosstab (M4S-5777 X120).
 * Spec: docs/pdlc/crosstabs-reuse.md §8.
 *
 * One rule turns a dataset into what the engine reads: a question catalog and respondent rows.
 *
 * - **A survey of its own.** The dataset is one source, `ext:<dataset id>`, beside any survey the
 *   crosstab loads. Its rows carry it as their survey, so a Survey / Wave banner, the Data source
 *   chips and the saved surveys (`surveyScope`) treat it as one more survey. An `ext:` id never
 *   reaches survey_portal: the data route reads it from the dataset store only.
 * - **What becomes a question.** A variable with value labels is a single-choice question, its
 *   answers the labels (and any value the data holds that has no label). One without labels and
 *   with at most `MAX_UNLABELLED_VALUES` values is a question whose answers are its values. Anything
 *   else (an identifier, open text, a number with many values) is listed as not brought.
 * - **Weights.** The file's weight variable (or the one named at upload) is each row's case weight,
 *   as SPSS applies it: a case with no or a non-positive weight is left out, and is counted in the
 *   list. The crosstab's weighted total is then the file's weighted N.
 *
 * Pure: no I/O.
 */

import type { CrosstabQuestionDto } from './question-formatter';
import type { SavDataset, SavVariableInfo } from './sav-reader';

/** A dataset's source id, as the crosstab and its saved surveys name it. */
export const OUTSIDE_PREFIX = 'ext:';
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
export const OUTSIDE_ID_RE = new RegExp(`^${OUTSIDE_PREFIX}${UUID}$`, 'i');

export const isOutsideSourceId = (id: unknown): id is string =>
    typeof id === 'string' && OUTSIDE_ID_RE.test(id.trim());
export const outsideSourceId = (datasetId: string) => `${OUTSIDE_PREFIX}${datasetId.toLowerCase()}`;
export const datasetIdOf = (sourceId: string) =>
    sourceId.trim().slice(OUTSIDE_PREFIX.length).toLowerCase();

/** The limits of an upload, stated in the spec and the dialog. */
export const OUTSIDE_LIMITS = {
    bytes: 25 * 1024 * 1024,
    rows: 100_000,
    questions: 500,
} as const;

/** A variable without labels becomes a question only with at most this many values. */
export const MAX_UNLABELLED_VALUES = 30;

/** The retention a dataset is kept for, in days, unless deleted sooner. */
export const OUTSIDE_RETENTION_DAYS = 180;

const ID_NAME_RE =
    /^(resp(ondent)?[_ ]?(id|no|num)?|id|uuid|case[_ ]?id|record[_ ]?id|panelist[_ ]?id|rid|pid)$/i;

/** One row of a dataset as stored: answers by question id, and its case weight. */
export interface OutsideRow {
    answers: Record<string, string>;
    weight: number | null;
}

export interface OutsideCatalog {
    questions: CrosstabQuestionDto[];
    rows: OutsideRow[];
    /** The weight variable applied, or null. */
    weightVariable: string | null;
    /** Sum of the weights of the rows kept (the rows kept when unweighted). */
    weightedTotal: number;
    /** What the file had that the crosstab cannot use, in words. */
    leftOut: string[];
}

/** A question id from a variable name: letters, digits and _, upper-cased, starting with a letter. */
export function questionIdOf(name: string, taken: Set<string>): string {
    let base = name
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '');
    if (!/^[A-Z]/.test(base)) base = `V${base}`;
    if (base.length > 64) base = base.slice(0, 64);
    let id = base || 'V';
    for (let n = 2; taken.has(id); n++) id = `${base}_${n}`;
    taken.add(id);
    return id;
}

/** A value as an answer code: an integer without decimals, a number as written, text as is. */
export function codeOf(value: number | string): string {
    if (typeof value === 'number')
        return Number.isInteger(value) ? String(value) : String(+value.toPrecision(12));
    return value.trim();
}

function answersOf(
    v: SavVariableInfo,
    values: (number | string)[],
): { code: string; label: string }[] | null {
    const out: { code: string; label: string }[] = [];
    const seen = new Set<string>();
    for (const vl of v.valueLabels) {
        const code = codeOf(vl.value);
        if (!code || seen.has(code.toUpperCase())) continue;
        seen.add(code.toUpperCase());
        out.push({ code, label: vl.label || code });
    }
    const observed = [...new Set(values.map(codeOf))].filter(
        (c) => c && !seen.has(c.toUpperCase()),
    );
    if (out.length > 0) {
        // Labelled: values the data holds without a label are answers too, after the labels.
        observed.sort(byValue).forEach((code) => out.push({ code, label: code }));
        return out;
    }
    if (observed.length === 0 || observed.length > MAX_UNLABELLED_VALUES) return null;
    return observed.sort(byValue).map((code) => ({ code, label: code }));
}

function byValue(a: string, b: string): number {
    const x = Number(a);
    const y = Number(b);
    if (Number.isFinite(x) && Number.isFinite(y)) return x - y;
    return a.localeCompare(b);
}

/**
 * Turn a dataset into the crosstab's catalog and rows. `weightVariable` overrides the file's own
 * (null: unweighted; undefined: the file's).
 */
export function outsideCatalog(
    ds: SavDataset,
    { sourceId, weightVariable }: { sourceId: string; weightVariable?: string | null },
): OutsideCatalog {
    const leftOut: string[] = [];
    const weightName = weightVariable === undefined ? ds.weightVariable : weightVariable;
    const wIndex = weightName
        ? ds.variables.findIndex((v) => v.name.toUpperCase() === weightName.toUpperCase())
        : -1;
    if (weightName && wIndex < 0) {
        leftOut.push(
            `The weight “${weightName}” is not a variable of the file, so it is not weighted.`,
        );
    }
    if (wIndex >= 0 && ds.variables[wIndex].type !== 'numeric') {
        leftOut.push(`The weight “${weightName}” is not a number, so it is not weighted.`);
    }
    const useWeight = wIndex >= 0 && ds.variables[wIndex].type === 'numeric';

    // Rows SPSS would leave out of a weighted analysis.
    const keep = ds.rows.map((r) => {
        if (!useWeight) return true;
        const w = r[wIndex];
        return typeof w === 'number' && w > 0;
    });
    const dropped = keep.filter((k) => !k).length;
    if (dropped) {
        leftOut.push(
            `${dropped.toLocaleString()} case${dropped === 1 ? ' has' : 's have'} no positive weight and ${dropped === 1 ? 'is' : 'are'} left out, as SPSS leaves ${dropped === 1 ? 'it' : 'them'} out of a weighted table.`,
        );
    }

    const taken = new Set<string>();
    const questions: CrosstabQuestionDto[] = [];
    const columns: { index: number; qid: string; codes: Map<string, string> }[] = [];
    ds.variables.forEach((v, index) => {
        if (index === wIndex) return;
        const values = ds.rows
            .filter((_, i) => keep[i])
            .map((r) => r[index])
            .filter((x): x is number | string => x !== null && x !== '');
        if (!v.valueLabels.length && ID_NAME_RE.test(v.name)) {
            leftOut.push(`${v.name} identifies each case, so it is not a question.`);
            return;
        }
        const answers = answersOf(v, values);
        if (!answers) {
            leftOut.push(
                values.length === 0
                    ? `${v.name} holds no answers, so it is not a question.`
                    : `${v.name} has ${new Set(values.map(codeOf)).size.toLocaleString()} different values and no value labels, so it is not a question. Group it, or give it value labels, and upload again.`,
            );
            return;
        }
        if (questions.length >= OUTSIDE_LIMITS.questions) {
            leftOut.push(
                `${v.name} is past the ${OUTSIDE_LIMITS.questions}-question limit, so it is not brought.`,
            );
            return;
        }
        const qid = questionIdOf(v.name, taken);
        const order = questions.length;
        questions.push({
            id: `${sourceId}:${qid}`,
            qid,
            question: v.label || v.name,
            wordingKey: v.label || v.name,
            sortOrder: order,
            surveyOrder: order,
            questionType: 'multiple_choice',
            selectMode: 'single',
            options: answers.map((a, i) => ({
                option_id: [a.code],
                option: a.label,
                sort_order: i,
            })),
            matrixOptions: {},
            surveyIds: [sourceId],
        });
        columns.push({
            index,
            qid,
            codes: new Map(answers.map((a) => [a.code.toUpperCase(), a.code])),
        });
    });

    const rows: OutsideRow[] = [];
    let weightedTotal = 0;
    ds.rows.forEach((r, i) => {
        if (!keep[i]) return;
        const answers: Record<string, string> = {};
        for (const c of columns) {
            const value = r[c.index];
            if (value === null || value === '') continue;
            const code = c.codes.get(codeOf(value).toUpperCase());
            if (code) answers[c.qid] = code;
        }
        const weight = useWeight ? (r[wIndex] as number) : null;
        weightedTotal += weight ?? 1;
        rows.push({ answers, weight });
    });

    return {
        questions,
        rows,
        weightVariable: useWeight ? ds.variables[wIndex].name : null,
        weightedTotal,
        leftOut,
    };
}

/** The response-table column that carries an outside row's case weight. Never a question. */
export const CASE_WEIGHT_COLUMN = '__CASE_WEIGHT';

/**
 * Lay a dataset's rows into a response table beside the survey's own: its questions as columns
 * (renamed where a survey question has the same id, `renames`), its rows with the dataset as their
 * survey, and the case weight column when weighted. Pure; the data route calls it.
 */
export function mergeOutsideIntoTable({
    table,
    sourceId,
    questions,
    rows,
    weighted,
}: {
    table: unknown[][];
    sourceId: string;
    questions: readonly CrosstabQuestionDto[];
    rows: readonly OutsideRow[];
    weighted: boolean;
}): unknown[][] {
    const header = [
        ...((table[0] as string[] | undefined) ?? ['PANELIST_ID', 'SURVEY_ID', 'DATE_RESPONSEDED']),
    ];
    const upperHeader = header.map((h) => String(h).toUpperCase());
    const columnOf = new Map<string, number>();
    for (const q of questions) {
        const at = upperHeader.indexOf(q.qid.toUpperCase());
        if (at >= 0) columnOf.set(q.qid, at);
        else {
            columnOf.set(q.qid, header.length);
            header.push(q.qid);
            upperHeader.push(q.qid.toUpperCase());
        }
    }
    let weightAt = upperHeader.indexOf(CASE_WEIGHT_COLUMN);
    if (weighted && weightAt < 0) {
        weightAt = header.length;
        header.push(CASE_WEIGHT_COLUMN);
    }
    const width = header.length;
    const pad = (row: unknown[]) => [
        ...row,
        ...new Array(Math.max(0, width - row.length)).fill(null),
    ];
    const out: unknown[][] = [header, ...table.slice(1).map((r) => pad(r as unknown[]))];
    rows.forEach((row, i) => {
        const cells: unknown[] = new Array(width).fill(null);
        cells[0] = `${sourceId}:${i + 1}`;
        cells[1] = sourceId;
        cells[2] = '';
        for (const [qid, code] of Object.entries(row.answers)) {
            const at = columnOf.get(qid);
            if (at !== undefined) cells[at] = code;
        }
        if (weighted && weightAt >= 0)
            cells[weightAt] = row.weight == null ? null : String(row.weight);
        out.push(cells);
    });
    return out;
}

/**
 * Rename a dataset's question ids that a survey question already uses (`AGE` → `AGE_X`), so the
 * two never share a column. Returns the renamed questions and rows.
 */
export function withoutClashes(
    taken: ReadonlySet<string>,
    questions: readonly CrosstabQuestionDto[],
    rows: readonly OutsideRow[],
): { questions: CrosstabQuestionDto[]; rows: OutsideRow[] } {
    const used = new Set([...taken].map((t) => t.toUpperCase()));
    const rename = new Map<string, string>();
    for (const q of questions) {
        let id = q.qid;
        if (used.has(id.toUpperCase())) {
            let n = 1;
            do id = `${q.qid}_X${n > 1 ? n : ''}`;
            while (used.has(id.toUpperCase()) && ++n);
        }
        used.add(id.toUpperCase());
        if (id !== q.qid) rename.set(q.qid, id);
    }
    if (!rename.size) return { questions: [...questions], rows: [...rows] };
    return {
        questions: questions.map((q) =>
            rename.has(q.qid) ? { ...q, qid: rename.get(q.qid)! } : q,
        ),
        rows: rows.map((r) => ({
            ...r,
            answers: Object.fromEntries(
                Object.entries(r.answers).map(([k, v]) => [rename.get(k) ?? k, v]),
            ),
        })),
    };
}

/**
 * The case weights a response table carries (`CASE_WEIGHT_COLUMN`), one per row after the header,
 * 1 for a row without one (a survey respondent beside an outside dataset), or undefined when the
 * table has no weight column. The engine reads it only when no weighting scheme is on.
 */
export function caseWeightsOf(table: readonly unknown[][]): number[] | undefined {
    const header = (table[0] as unknown[] | undefined) ?? [];
    const at = header.findIndex((h) => String(h ?? '').toUpperCase() === CASE_WEIGHT_COLUMN);
    if (at < 0) return undefined;
    let any = false;
    const out = table.slice(1).map((row) => {
        const n = Number((row as unknown[])[at]);
        if (Number.isFinite(n) && n > 0) {
            any = true;
            return n;
        }
        return 1;
    });
    return any ? out : undefined;
}

/** One dataset the data route read, as the merge needs it. */
export interface OutsideSource {
    datasetId: string;
    name: string;
    projectId: number;
    questions: CrosstabQuestionDto[];
    rows: OutsideRow[];
    weightVariable: string | null;
    weightedTotal: number;
    leftOut: string[];
}

/** What the workspace is told about each outside source it loaded. */
export interface OutsideSourceInfo {
    id: string;
    name: string;
    rows: number;
    weightVariable: string | null;
    weightedTotal: number;
    leftOut: string[];
}

type DataPayload = {
    questions?: CrosstabQuestionDto[];
    responses?: unknown[][];
    surveyIds?: string[];
    surveyNs?: Record<string, number>;
    totalRespondents?: number;
    pivotedQids?: string[];
    surveyInfo?: Record<string, { projectId: string | null; name: string | null }>;
    wholeProject?: boolean;
    numericProjectId?: number | null;
};

/**
 * The data route's answer with its outside sources laid in beside the surveys: their questions
 * (renamed where a survey question has the id), their rows, each as one more survey, and what was
 * asked for and could not be read (`outsideMissing`). A crosstab with an outside source is never
 * its "whole project", so its surveys are always saved with it.
 */
export function mergeOutsideIntoPayload<T extends DataPayload>(
    payload: T,
    sources: readonly OutsideSource[],
    missing: readonly string[] = [],
): T & DataPayload & { outsideSources?: OutsideSourceInfo[]; outsideMissing?: string[] } {
    if (!sources.length && !missing.length) return payload;
    let questions = [...(payload.questions ?? [])];
    let responses = payload.responses ?? [['PANELIST_ID', 'SURVEY_ID', 'DATE_RESPONSEDED']];
    const surveyIds = [...(payload.surveyIds ?? [])];
    const surveyNs = { ...(payload.surveyNs ?? {}) };
    const surveyInfo = { ...(payload.surveyInfo ?? {}) };
    const pivoted = [...(payload.pivotedQids ?? [])];
    let total = payload.totalRespondents ?? 0;
    const info: OutsideSourceInfo[] = [];
    for (const src of sources) {
        const sourceId = outsideSourceId(src.datasetId);
        const taken = new Set(questions.map((q) => q.qid.toUpperCase()));
        const clean = withoutClashes(taken, src.questions, src.rows);
        const order = questions.length;
        questions = [
            ...questions,
            ...clean.questions.map((q, i) => ({
                ...q,
                surveyIds: [sourceId],
                surveyOrder: order + i,
            })),
        ];
        responses = mergeOutsideIntoTable({
            table: responses,
            sourceId,
            questions: clean.questions,
            rows: clean.rows,
            weighted: !!src.weightVariable,
        });
        surveyIds.push(sourceId);
        surveyNs[sourceId] = clean.rows.length;
        surveyInfo[sourceId] = { projectId: String(src.projectId), name: src.name };
        pivoted.push(...clean.questions.map((q) => q.qid));
        total += clean.rows.length;
        info.push({
            id: sourceId,
            name: src.name,
            rows: clean.rows.length,
            weightVariable: src.weightVariable,
            weightedTotal: src.weightedTotal,
            leftOut: src.leftOut,
        });
    }
    const out = {
        ...payload,
        questions,
        responses,
        surveyIds,
        surveyNs,
        surveyInfo,
        pivotedQids: pivoted,
        totalRespondents: total,
        ...(payload.numericProjectId == null && sources[0]
            ? { numericProjectId: sources[0].projectId }
            : {}),
        ...(sources.length && payload.wholeProject === true ? { wholeProject: false } : {}),
        ...(info.length ? { outsideSources: info } : {}),
        ...(missing.length ? { outsideMissing: [...missing] } : {}),
    };
    return out;
}
